import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';

// Project status definitions
export const PROJECT_STATUSES = {
  ACTIVE: 'active',
  ON_HOLD: 'on_hold',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

export function useProjects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Projects are private - only show current user's projects
    if (!user?.uid) {
      setProjects([]);
      setLoading(false);
      return;
    }

    // Query by userId (new multi-tenant field)
    const q = query(
      collection(db, 'projects'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const projectsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setProjects(projectsData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching projects:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  const addProject = async (projectData) => {
    if (!user?.uid) {
      throw new Error('User must be logged in to add projects');
    }

    try {
      const newProject = {
        name: projectData.name || '',
        description: projectData.description || '',
        status: projectData.status || PROJECT_STATUSES.ACTIVE,
        goalId: projectData.goalId || null, // Link to parent goal
        userId: user.uid, // Multi-tenant: owner by Firebase UID
        dueDate: projectData.dueDate || null,
        // Carry over source info if converted from inbox item
        source: projectData.source || 'manual',
        sourceChannel: projectData.sourceChannel || null,
        sourceLink: projectData.sourceLink || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'projects'), newProject);
      return docRef.id;
    } catch (err) {
      console.error('Error adding project:', err);
      throw err;
    }
  };

  const updateProject = async (id, projectData) => {
    try {
      const projectRef = doc(db, 'projects', id);
      await updateDoc(projectRef, {
        ...projectData,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error updating project:', err);
      throw err;
    }
  };

  const deleteProject = async (id) => {
    try {
      await deleteDoc(doc(db, 'projects', id));
    } catch (err) {
      console.error('Error deleting project:', err);
      throw err;
    }
  };

  const completeProject = async (id) => {
    await updateProject(id, { status: PROJECT_STATUSES.COMPLETED });
  };

  // Filter helpers
  const getActiveProjects = useCallback(() => {
    return projects.filter(p => p.status === PROJECT_STATUSES.ACTIVE);
  }, [projects]);

  const getProjectsByGoal = useCallback((goalId) => {
    return projects.filter(p => p.goalId === goalId);
  }, [projects]);

  const getCompletedProjects = useCallback(() => {
    return projects.filter(p => p.status === PROJECT_STATUSES.COMPLETED);
  }, [projects]);

  return {
    projects,
    loading,
    error,
    addProject,
    updateProject,
    deleteProject,
    completeProject,
    getActiveProjects,
    getProjectsByGoal,
    getCompletedProjects
  };
}
